/**
 * Lokalisierungs-Helfer. Locale ist fix de-AT.
 * Zeitzone ist fix Europe/Vienna (Single-User-Tool, lokal in AT betrieben).
 */

import type {
  MailNotificationStatus,
  MailTemplateTrigger,
  SystemScope,
  UpdatePolicy,
} from '@/types/api';

const LOCALE = 'de-AT';
const TIMEZONE = 'Europe/Vienna';

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: TIMEZONE,
});

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: 'medium',
  timeZone: TIMEZONE,
});

const relativeTimeFormatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

const numberFormatter = new Intl.NumberFormat(LOCALE);

const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return dateTimeFormatter.format(d);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return dateFormatter.format(d);
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '–';
  return numberFormatter.format(value);
}

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '–';
  return currencyFormatter.format(value);
}

// --- System-Enum-Labels (zentral, damit alle Views identisch beschriften) ---

export const POLICY_LABELS: Record<UpdatePolicy, string> = {
  auto: 'Automatisch',
  manual: 'Manuell',
  never: 'Nie',
  coordinated: 'Koordiniert',
};

export const SCOPE_LABELS: Record<SystemScope, string> = {
  in_scope: 'Im Scope',
  manual_only: 'Nur manuell',
  excluded: 'Ausgeschlossen',
  eol: 'EOL',
  offline: 'Offline',
  external: 'Extern',
};

/**
 * Deutsches Label für die Update-Policy. Fehlt der Wert, greift der
 * Schema-Default `auto` -> "Automatisch" (statt des früheren Rohwerts "yes").
 */
export function policyLabel(policy: UpdatePolicy | null | undefined): string {
  if (!policy) return POLICY_LABELS.auto;
  return POLICY_LABELS[policy] ?? policy;
}

/** Deutsches Label für den Wartungs-Scope. Fehlt der Wert -> "Im Scope". */
export function scopeLabel(scope: SystemScope | null | undefined): string {
  if (!scope) return SCOPE_LABELS.in_scope;
  return SCOPE_LABELS[scope] ?? scope;
}

const MODE_LABELS: Record<string, string> = {
  check: 'Trockenlauf',
  apply: 'Echtlauf',
  rollback: 'Rollback',
};

/**
 * Deutsches Label für den Run-Modus.
 *
 * Die Rohwerte 'check' und 'apply' kollidieren mit den Routinen-NAMEN: eine
 * Zeile "Proxmox Update-Check | apply" liest sich wie ein Widerspruch, obwohl
 * beides stimmt (eine nur lesende Routine, echt ausgeführt). Deutsche Labels
 * lösen die Doppeldeutigkeit auf.
 */
export function runModeLabel(mode: string | null | undefined): string {
  if (!mode) return '–';
  return MODE_LABELS[mode] ?? mode;
}

// --- Mail-Enum-Labels ------------------------------------------------------

export const MAIL_TRIGGER_LABELS: Record<MailTemplateTrigger, string> = {
  // Die drei Fenster-Auslöser: eine Mail je Wartungsabend, nicht je Lauf.
  wartung_gestartet: 'Wartung gestartet',
  wartung_abgeschlossen: 'Wartung abgeschlossen',
  wartung_bericht: 'Wartungsbericht (auf Nachfrage)',
  // Nacherfassung von Hand: eigene Vorlage, weil die Abschlussmail Läufe und
  // Routinen zählt - für ein Fenster ohne Lauf stünde dort überall 0.
  wartung_manuell: 'Wartung (manuell erfasst)',
  // run_started/run_finished werden seit dem Mail-Spam vom 11.08.2026 nicht
  // mehr ausgelöst, müssen aber im Postausgang weiter lesbar bleiben.
  run_started: 'Lauf gestartet (nicht mehr aktiv)',
  run_finished: 'Lauf abgeschlossen (nicht mehr aktiv)',
  maintenance_due: 'Wartung fällig',
  manual: 'Manuell',
};

/** Deutsches Label für den Mail-Auslöser; unbekannte Werte roh durchreichen. */
export function mailTriggerLabel(trigger: string | null | undefined): string {
  if (!trigger) return '–';
  return MAIL_TRIGGER_LABELS[trigger as MailTemplateTrigger] ?? trigger;
}

export const NOTIFICATION_STATUS_LABELS: Record<MailNotificationStatus, string> = {
  draft: 'Entwurf',
  sent: 'Gesendet',
  failed: 'Fehlgeschlagen',
  discarded: 'Verworfen',
};

export function notificationStatusLabel(
  status: MailNotificationStatus | null | undefined
): string {
  if (!status) return '–';
  return NOTIFICATION_STATUS_LABELS[status] ?? status;
}

/**
 * Liefert `vor 3 Tagen`, `in 2 Stunden` o.ae.
 */
export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  const diffMs = d.getTime() - now.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return relativeTimeFormatter.format(diffSec, 'second');
  if (abs < 3600) return relativeTimeFormatter.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86_400) return relativeTimeFormatter.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 2_592_000) return relativeTimeFormatter.format(Math.round(diffSec / 86_400), 'day');
  if (abs < 31_536_000)
    return relativeTimeFormatter.format(Math.round(diffSec / 2_592_000), 'month');
  return relativeTimeFormatter.format(Math.round(diffSec / 31_536_000), 'year');
}
